import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { green } from "@mui/material/colors";

// Styled form container
const LoginForm = styled("form")(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(1),
  display: "flex",
  flexWrap: "wrap",
}));

// Styled loading spinner
const ButtonProgress = styled(CircularProgress)({
  color: green[500],
  position: "absolute",
  top: "50%",
  left: "50%",
  marginTop: -12,
  marginLeft: -12,
});

interface LoginDialogProps {
  LoginVisible: boolean;
  loginState: string;
  hideLogin: () => void;
  authenticateUser: (username: string, password: string) => void;
}

export default function LoginDialog({
  LoginVisible,
  loginState,
  hideLogin,
  authenticateUser,
}: LoginDialogProps) {
  const [textValues, setTextValues] = useState({ username: "", password: "" });

  const handleTextChange =
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setTextValues({ ...textValues, [name]: event.target.value });
    };

  // Reset username and password when dialog visibility changes
  useEffect(() => {
    setTextValues({ username: "", password: "" });
    return () => {
      setTextValues({ username: "", password: "" });
    };
  }, [LoginVisible]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (
      event.charCode === 13 &&
      textValues.username &&
      textValues.password
    ) {
      authenticateUser(textValues.username, textValues.password);
    }
  };

  const isLoginDisabled =
    textValues.username.trim().length === 0 ||
    textValues.password.trim().length === 0;

  return (
    <Dialog
      open={LoginVisible}
      aria-labelledby="login-title"
      onKeyPress={handleKeyPress}
    >
      <DialogTitle id="login-title">Login</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please enter username and password
        </DialogContentText>
        <LoginForm noValidate>
          <TextField
            value={textValues.username}
            onChange={handleTextChange("username")}
            margin="normal"
            required
            fullWidth
            id="email"
            label="Username"
            name="email"
            autoComplete="email"
            autoFocus
            sx={{ mx: 1 }}
          />
          <TextField
            value={textValues.password}
            onChange={handleTextChange("password")}
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            sx={{ mx: 1 }}
          />
        </LoginForm>
        {loginState === "Failed" && (
          <DialogContentText color="error">Login failed</DialogContentText>
        )}
        {loginState === "RateLimited" && (
          <DialogContentText color="error">
            Too many login attempts. Try again shortly.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={hideLogin} color="primary" sx={{ my: 3 }}>
          Cancel
        </Button>
        <Button
          onClick={() => authenticateUser(textValues.username, textValues.password)}
          color="primary"
          disabled={isLoginDisabled}
          type="submit"
          sx={{ my: 3 }}
        >
          Login
        </Button>
        {loginState === "InProgress" && <ButtonProgress size={24} />}
      </DialogActions>
    </Dialog>
  );
}
